import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Spin,
  Typography,
  message,
} from "antd";
import { InfoCircleOutlined, CalendarOutlined, DollarOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import dayjs, { Dayjs } from "dayjs";
import axiosInstance from "../../../utils/axiosInstance";
import { uploadFileToS3 } from "../../../utils/s3Upload";
import { DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD } from "../../../constants/common";
import { extractSyntheticControlInput } from "../utils/engineDataExtraction";
import { StepUploadData } from "./StepUpload";

const { Text } = Typography;
const { Option } = Select;

export interface StepConfigurationData {
  jobName: string;
  // Bid Optimizer Test Setup
  campaignObjective: string;
  customerIdentifier: string;
  targetVariable: string;
  age?: string;
  gender?: string;
  devicePlatform?: string;
  geoLocationType: "dma" | "region" | "state";
  region: string;
  locale?: string;
  userOs?: string;
  // Geo Test Setup
  dateColumn: string;
  campaignStartDate: string;
  bauSpent: number;
  statSigThreshold: number;
  scmType: "single" | "pooled";
  isSales: boolean;
  selectedMarkets: string[];
}

interface StepConfigurationProps {
  stepUploadData?: StepUploadData | null;
  jobConfigId: string;
  onNext: (data: StepConfigurationData) => void;
  onPrevious: () => void;
  onNameChange?: (name: string) => void;
  existingData?: StepConfigurationData | null;
  // True when opened via "View" on an already-created job: the raw uploaded
  // file isn't available in this browser session (only stepUploadData's parsed
  // headers/File would let us re-detect columns/markets), so the form is shown
  // read-only from the job's already-saved config and Next just advances
  // straight to Power Analysis rather than re-running column detection,
  // re-aggregating geo_lift_data.csv, or re-submitting a job (that only ever
  // happens once, at creation, via StepProcessing.tsx).
  viewMode?: boolean;
}

const guessColumn = (headers: string[], candidates: RegExp[]): string | undefined => {
  for (const pattern of candidates) {
    const match = headers.find((h) => pattern.test(h));
    if (match) return match;
  }
  return undefined;
};

// Step 2 of the wireframe, three clearly separated blocks in order: General
// Information (Job Name) -> Bid Optimizer Test Setup (one card, matching the
// wireframe's single-subsection layout - not StepTwo.tsx's per-field-group
// cards) -> Geo Test Setup (numbered white cards, matching PreCampaign.tsx).
// Date Column is auto-detected silently (no dropdown - per user request), same
// assumption the original wireframe made. Statistical Significance is a plain
// text Input with a default + on-blur clamp, exactly matching PreCampaign.tsx's
// own field (not a dropdown - Geo Lift accepts any threshold, not a fixed set).
// The Target Variable and Region/Geo Location columns picked here double as
// GeoLift's kpi_variables/location_variables so the user isn't asked to pick the
// same column twice under two different names.
const StepConfiguration: React.FC<StepConfigurationProps> = ({
  stepUploadData,
  jobConfigId,
  onNext,
  onPrevious,
  onNameChange,
  existingData,
  viewMode,
}) => {
  const headers = stepUploadData?.headers || [];
  const [form] = Form.useForm();

  const dateColumn = useMemo(
    () => existingData?.dateColumn || guessColumn(headers, [/date/i, /week/i, /^day$/i, /period/i]) || "",
    [headers, existingData?.dateColumn]
  );
  const [campaignStartDate, setCampaignStartDate] = useState<Dayjs | null>(
    existingData?.campaignStartDate ? dayjs(existingData.campaignStartDate) : null
  );
  const [bauSpent, setBauSpent] = useState<number | null>(existingData?.bauSpent ?? null);
  const [statSigThreshold, setStatSigThreshold] = useState<string>(
    existingData?.statSigThreshold !== undefined
      ? String(existingData.statSigThreshold)
      : `${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`
  );
  const [scmType, setScmType] = useState<"single" | "pooled">(existingData?.scmType || "single");
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(
    existingData?.selectedMarkets || []
  );
  const [marketOptions, setMarketOptions] = useState<string[]>([]);
  const [parsingMarkets, setParsingMarkets] = useState(false);
  const [suggestingMarkets, setSuggestingMarkets] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [preparingGeoLiftData, setPreparingGeoLiftData] = useState(false);
  const [geoLiftDataReady, setGeoLiftDataReady] = useState(false);
  const [geoLiftDataError, setGeoLiftDataError] = useState<string | null>(null);
  const lastPreparedKeyRef = useRef<string>("");

  // In view mode, existingData arrives asynchronously (fetched by the parent
  // after this component has already mounted with existingData=null) - the
  // useState initializers above only run once, on mount, so without this they
  // stay stuck at their "no data yet" defaults forever (Select Markets shows
  // empty, BAU spend/campaign date/stat sig/SCM type never populate) even
  // after the real data arrives as a later prop update. Re-syncs whenever
  // existingData changes to a real object. Also benefits the creation flow's
  // own Previous/Next round-trip through this step within one session.
  useEffect(() => {
    if (!existingData) return;
    setCampaignStartDate(existingData.campaignStartDate ? dayjs(existingData.campaignStartDate) : null);
    setBauSpent(existingData.bauSpent ?? null);
    setStatSigThreshold(
      existingData.statSigThreshold !== undefined
        ? String(existingData.statSigThreshold)
        : `${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`
    );
    setScmType(existingData.scmType || "single");
    setSelectedMarkets(existingData.selectedMarkets || []);
  }, [existingData]);

  // Full set of distinct values in the chosen Region/Geo Location column, parsed
  // from the full uploaded file (Step 1's preview only has 10 rows) - these
  // become the Select's selectable options, exactly like PreCampaign.tsx's
  // locationData-derived options list.
  const region = Form.useWatch("region", form);
  const targetVariable = Form.useWatch("targetVariable", form);
  const campaignObjective = Form.useWatch("campaignObjective", form);
  // Measuring Sales Revenue is derived from Campaign Objective rather than a
  // separate toggle - "Sales" objective means the Y column is revenue (isSales
  // controls $ vs iROAS display, per project memory), any other objective means
  // it isn't. One field driving both engines' notion of "is this a sales test".
  const isSales = campaignObjective === "sales";

  useEffect(() => {
    if (viewMode || !stepUploadData?.dataFile) return;
    if (!region) {
      setMarketOptions([]);
      return;
    }
    setParsingMarkets(true);
    Papa.parse(stepUploadData.dataFile, {
      header: true,
      complete: (results) => {
        const values = new Set<string>();
        (results.data as any[]).forEach((row) => {
          const v = row?.[region];
          if (v !== undefined && v !== null && String(v).trim() !== "") {
            values.add(String(v).trim());
          }
        });
        setMarketOptions(Array.from(values).sort());
        setParsingMarkets(false);
      },
      error: () => setParsingMarkets(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  // Once Date Column + Region + Target Variable are all known, two things
  // happen in sequence, automatically (no button):
  //
  // 1. extractSyntheticControlInput aggregates the shared file down to one row
  //    per (date, location) and uploads it as this job's geo_lift_data.csv.
  //    GeoLift's own code has always assumed that shape (one row per location
  //    per date) - the raw shared file violates it (many rows per location per
  //    date, from Bid Optimizer's segment columns), which silently produces
  //    wrong period counts deep inside GeoLift's existing pipeline rather than
  //    a clean error (see causal-loop-feature/plan.md). This restores the
  //    shape GeoLift expects without touching GeoLift's own code, and is
  //    Causal-Loop-only - the standalone Geo Lift upload flow is untouched.
  // 2. Once that's uploaded, suggest-markets runs against it - the same
  //    DTW-based clustering the standalone Geo Lift flow calls right after its
  //    own upload/column-mapping step (PreCampaign.tsx:
  //    handleFileUpload -> processLocationsAfterUpload ->
  //    getPreselectedTestMarkets, automatically, no button there either).
  //    Pre-selects the result into Select Markets rather than replacing its
  //    option list - same relationship as PreCampaign.tsx's
  //    selectedMarketsIncludes vs. locationData.
  //
  // Re-runs (not just once) if the user changes any of these three fields
  // afterward, so the uploaded file and suggestions stay in sync with the
  // current choices - already-selected markets are never overwritten (see
  // setSelectedMarkets below), only pre-filled when still empty.
  useEffect(() => {
    if (viewMode || !stepUploadData?.dataFile) return;
    if (!dateColumn || !region || !targetVariable) return;
    const key = `${dateColumn}|${region}|${targetVariable}`;
    if (lastPreparedKeyRef.current === key) return;
    lastPreparedKeyRef.current = key;

    (async () => {
      setPreparingGeoLiftData(true);
      setGeoLiftDataError(null);
      setGeoLiftDataReady(false);
      try {
        const aggregatedFile = await extractSyntheticControlInput(
          stepUploadData!.dataFile,
          dateColumn,
          region,
          targetVariable
        );
        await uploadFileToS3(aggregatedFile, `${jobConfigId}/input/geo_lift_data.csv`);
        setGeoLiftDataReady(true);
      } catch (err) {
        console.error("Error preparing Geo Lift data:", err);
        setGeoLiftDataError("Couldn't prepare Geo Lift data from your file. Please try again or go back to Step 1.");
        setPreparingGeoLiftData(false);
        return;
      }
      setPreparingGeoLiftData(false);

      setSuggestingMarkets(true);
      setSuggestError(null);
      try {
        const response = await axiosInstance.post("/api/causal-loop/suggest-markets", {
          jobConfigId,
          dateColumn,
          locationColumn: region,
          targetColumn: targetVariable,
          campaignStartDate: campaignStartDate ? campaignStartDate.format("YYYY-MM-DD") : undefined,
        });
        const suggested: string[] = response.data?.marketNames || [];
        if (suggested.length > 0) {
          setSelectedMarkets((prev) => (prev.length > 0 ? prev : suggested));
        }
      } catch (err: any) {
        setSuggestError(
          err?.response?.data?.detail || "Couldn't auto-suggest markets - pick manually below."
        );
      } finally {
        setSuggestingMarkets(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateColumn, region, targetVariable]);

  const handleStatSigBlur = () => {
    if (statSigThreshold.trim() === "") {
      setStatSigThreshold(`${DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD}`);
      return;
    }
    const numericValue = Number(statSigThreshold);
    if (Number.isNaN(numericValue) || numericValue < 50) {
      setStatSigThreshold("50");
    } else if (numericValue > 100) {
      setStatSigThreshold("100");
    }
  };

  const canProceedGeo = useMemo(() => {
    return Boolean(
      dateColumn &&
        campaignStartDate &&
        bauSpent !== null &&
        bauSpent > 0 &&
        selectedMarkets.length > 0 &&
        geoLiftDataReady
    );
  }, [dateColumn, campaignStartDate, bauSpent, selectedMarkets, geoLiftDataReady]);

  const handleFinish = (values: any) => {
    if (!dateColumn) {
      message.error("No date column could be detected in your file - please go back and upload a file with a date/time column.");
      return;
    }
    if (preparingGeoLiftData) {
      message.info("Still preparing your Geo Lift data - just a moment.");
      return;
    }
    if (!canProceedGeo) {
      message.error("Please complete the Geo Test Setup fields below (campaign start date, BAU spend, and at least one market).");
      return;
    }
    onNext({
      jobName: values.jobName.trim(),
      campaignObjective: values.campaignObjective,
      customerIdentifier: values.customerIdentifier,
      targetVariable: values.targetVariable,
      age: values.age,
      gender: values.gender,
      devicePlatform: values.devicePlatform,
      geoLocationType: values.geoLocationType || "dma",
      region: values.region,
      locale: values.locale,
      userOs: values.userOs,
      dateColumn,
      campaignStartDate: (campaignStartDate as Dayjs).format("YYYY-MM-DD"),
      bauSpent: bauSpent as number,
      statSigThreshold: Number(statSigThreshold),
      scmType,
      isSales,
      selectedMarkets,
    });
  };

  const handleValuesChange = (changed: any) => {
    if (changed.jobName !== undefined) onNameChange?.(changed.jobName);
  };

  // In view mode there's no uploaded file to derive a header list from - fall
  // back to just the columns this job actually has values for, so each
  // (disabled) Select still has a matching Option to display.
  const viewOnlyHeaders = useMemo(
    () =>
      Array.from(
        new Set(
          [
            existingData?.customerIdentifier,
            existingData?.targetVariable,
            existingData?.age,
            existingData?.gender,
            existingData?.devicePlatform,
            existingData?.region,
            existingData?.locale,
            existingData?.userOs,
          ].filter(Boolean) as string[]
        )
      ),
    [existingData]
  );
  const columnOptions = (viewMode ? viewOnlyHeaders : headers).map((h) => (
    <Option key={h} value={h}>
      {h}
    </Option>
  ));
  const marketSelectOptions = viewMode ? selectedMarkets : marketOptions;

  if (viewMode && !existingData) {
    return (
      <div className="text-center py-24">
        <Spin size="large" tip="Loading saved configuration..." />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={viewMode}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      initialValues={{
        jobName: existingData?.jobName || "",
        campaignObjective: existingData?.campaignObjective || "sales",
        customerIdentifier:
          existingData?.customerIdentifier ||
          guessColumn(headers, [/cust.*id/i, /customer.*id/i, /user.*id/i, /^id$/i]),
        targetVariable:
          existingData?.targetVariable || guessColumn(headers, [/target/i, /sales/i, /revenue/i, /^y$/i]),
        age: existingData?.age || guessColumn(headers, [/age/i]),
        gender: existingData?.gender || guessColumn(headers, [/gender/i]),
        devicePlatform: existingData?.devicePlatform || guessColumn(headers, [/device/i]),
        geoLocationType: existingData?.geoLocationType || "dma",
        region:
          existingData?.region ||
          guessColumn(headers, [/location/i, /dma/i, /region/i, /city/i, /state/i, /geo/i]),
        locale: existingData?.locale || guessColumn(headers, [/locale/i]),
        userOs: existingData?.userOs || guessColumn(headers, [/^os$|user.*os/i]),
      }}
    >
      {/* --- Block 1: General Information --- */}
      <Card title={<span className="text-lg font-semibold text-gray-900">General Information</span>} className="mb-6 shadow-sm">
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="jobName"
              label="Job Name"
              rules={[
                { required: true, message: "Please enter a name" },
                { max: 120, message: "Name cannot exceed 120 characters" },
              ]}
              tooltip={{
                title: "One study, two engines - covers both Bid Optimizer and Geo Lift results.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input placeholder="e.g. Fall Sales Push - Bid + Geo Study" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="campaignObjective"
              label="Campaign Objective"
              rules={[{ required: true, message: "Please select an objective" }]}
              tooltip={{
                title:
                  "Drives Bid Optimizer's optimization target and Geo Test's \"Measuring Sales Revenue\" setting - Sales means both treat the target column as revenue.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                options={[
                  { label: "Sales", value: "sales" },
                  { label: "Lead Generation", value: "lead" },
                  { label: "Reach", value: "reach" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* --- Block 2: Bid Optimizer Test Setup (one card, tiered by badge) --- */}
      <Card title={<span className="text-lg font-semibold text-gray-900">Bid Optimizer Test Setup</span>} className="mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Text strong className="text-base font-semibold text-gray-800">Required Variables</Text>
          <Badge count="Required" style={{ backgroundColor: "#1890ff" }} />
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="customerIdentifier"
              label="Customer Identifier"
              rules={[{ required: true, message: "Please select a Customer Identifier column" }]}
              tooltip={{
                title: "Customer id for unique identification of records. Eg: Id or email",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select placeholder="Select column" showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="targetVariable"
              label="Target Variable"
              rules={[{ required: true, message: "Please select a Target Variable column" }]}
              tooltip={{
                title: "Column the model optimizes against - also used as Geo Lift's KPI. Eg: Sales",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select placeholder="Select column" showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <Text strong className="text-base font-semibold text-gray-800">Demographic Variables</Text>
          <Badge count="Recommended" style={{ backgroundColor: "#52c41a" }} />
        </div>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item name="age" label="Age Range">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="gender" label="Gender">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="locale" label="Locale">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <Text strong className="text-base font-semibold text-gray-800">Geographic Variables</Text>
          <Badge count="Recommended" style={{ backgroundColor: "#52c41a" }} />
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="geoLocationType"
              label="Geo Location Type"
              tooltip={{
                title: "Select the geo location type",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                placeholder="Select type"
                allowClear
                options={[
                  { label: "Designated Market Area", value: "dma" },
                  { label: "Regions", value: "region" },
                  { label: "States", value: "state" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="region"
              label="Region / Geo Location"
              rules={[{ required: true, message: "Please select the Region/Geo Location column" }]}
              tooltip={{
                title: "This column is also used as Geo Lift's location column below.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select placeholder="Select column" showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <Text strong className="text-base font-semibold text-gray-800">Device Variables</Text>
          <Badge count="Optional" style={{ backgroundColor: "#faad14" }} />
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="devicePlatform" label="Device Platform">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="userOs" label="User OS">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* --- Block 3: Geo Test Setup - matches PreCampaign.tsx's numbered white-card style --- */}
      <h3 className="text-lg font-semibold text-gray-900 mb-3">Geo Test Setup</h3>

      {!dateColumn && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          message="No date/time column detected"
          description="Geo Lift (Synthetic Control) needs a date column per location to compare pre- and post-campaign performance - none of this file's columns look like a date. Please go back to Step 1 and upload a file that includes one."
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <CalendarOutlined className="text-indigo-600" />
            Campaign Start Date
            <span className="text-red-500">*</span>
          </label>
          <DatePicker
            placeholder="Select when your campaign started"
            value={campaignStartDate}
            onChange={setCampaignStartDate}
            size="large"
            className="w-full rounded-lg"
            format="YYYY-MM-DD"
            disabled={viewMode}
          />
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <DollarOutlined className="text-green-600" />
            Avg. Monthly BAU Spend
            <span className="text-red-500">*</span>
          </label>
          <InputNumber
            placeholder="e.g. 180000"
            value={bauSpent ?? undefined}
            onChange={(v) => setBauSpent(v as number)}
            size="large"
            className="w-full rounded-lg"
            min={0}
            prefix="$"
            disabled={viewMode}
          />
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            Statistical Significance Threshold %
          </label>
          <Input
            placeholder="Enter Statistical Significance Threshold % (50 to 100 inclusive)"
            value={statSigThreshold}
            onChange={(e) => setStatSigThreshold(e.target.value)}
            onBlur={handleStatSigBlur}
            size="large"
            type="text"
            className="rounded-lg"
            disabled={viewMode}
          />
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            SCM Type
          </label>
          <Select
            value={scmType}
            onChange={setScmType}
            size="large"
            className="w-full"
            disabled={viewMode}
          >
            <Option value="single">Single SCM</Option>
            <Option value="pooled">Pooled SCM</Option>
          </Select>
        </div>
      </div>

      {scmType === "single" && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow mb-6">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            Select Markets
            <span className="text-red-500">*</span>
            {preparingGeoLiftData && (
              <span className="flex items-center gap-2 text-sm font-normal text-indigo-600 ml-2">
                <Spin size="small" /> Preparing Geo Lift data...
              </span>
            )}
            {!preparingGeoLiftData && suggestingMarkets && (
              <span className="flex items-center gap-2 text-sm font-normal text-indigo-600 ml-2">
                <Spin size="small" /> Finding candidate markets...
              </span>
            )}
          </label>
          {geoLiftDataError && <Alert type="error" showIcon message={geoLiftDataError} className="mb-3" />}
          {suggestError && <Alert type="warning" showIcon message={suggestError} className="mb-3" />}
          <Select
            mode="multiple"
            placeholder={region ? "Select one or more markets" : "Choose a Region column above first"}
            value={selectedMarkets}
            onChange={setSelectedMarkets}
            loading={parsingMarkets}
            disabled={viewMode || !region}
            size="large"
            className="w-full"
            maxTagCount="responsive"
            options={marketSelectOptions.map((m) => ({ label: m, value: m }))}
          />
        </div>
      )}

      <div className="flex justify-between">
        {/* Explicit disabled={false} on both buttons below: antd's Button reads
            the same DisabledContext that <Form disabled={viewMode}> sets on
            its descendants, so without overriding it here, both buttons would
            silently inherit disabled=true from the Form in view mode - an
            explicit disabled prop always wins over the inherited context. */}
        <Button size="large" onClick={onPrevious} disabled={false}>
          Previous
        </Button>
        {viewMode ? (
          <Button
            type="primary"
            size="large"
            disabled={false}
            onClick={() => onNext(existingData as StepConfigurationData)}
          >
            Next
          </Button>
        ) : (
          <Button type="primary" size="large" htmlType="submit" disabled={!canProceedGeo}>
            Calculate Power
          </Button>
        )}
      </div>
    </Form>
  );
};

export default StepConfiguration;
